import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications } from '@/stores/useNotificationStore';
import { logger } from '@equip/utils/logger';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import {
  useTriggerPeopleSync,
  useCheckSyncStatus,
  useGetLastSync,
} from '@/hooks/mutations/person/usePeopleSyncMutation';

const log = logger.child({ component: 'PersonSyncModal' });

// TODO: FUTURE - Add comprehensive test coverage for PersonSyncModal including:
//   - Sync state transitions (idle -> starting -> running -> completed/failed/timeout)
//   - Polling logic and timeout handling
//   - Error display and handling
//   - UI interactions (start sync, close modal, etc.)

// Configuration constants for polling and timeouts
const POLL_CONFIG = {
  // Poll every 20 seconds for status updates
  POLL_INTERVAL_MS: 20000,
  // Maximum polling duration (1 hour)
  MAX_POLL_DURATION_MS: 60 * 60 * 1000,
} as const;

interface SyncResult {
  totalFetched: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  errorDetails: Array<{ contactId: string; error: string }>;
  durationMs: number;
  syncedAt: string;
}

interface PersonSyncModalProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

type SyncStatus = 'idle' | 'starting' | 'running' | 'completed' | 'failed' | 'timeout';

const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }

  return `${minutes} minute${minutes !== 1 ? 's' : ''} ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`;
};

export const PersonSyncModal: React.FC<PersonSyncModalProps> = ({ open, onClose, onComplete }) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const [status, setStatus] = useState<SyncStatus>('idle');
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [result, setResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const pollAttemptsRef = useRef(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // React Query mutations
  const triggerSync = useTriggerPeopleSync();
  const checkStatus = useCheckSyncStatus();
  const getLastSync = useGetLastSync();
  const checkStatusMutateAsyncRef = useRef(checkStatus.mutateAsync);

  useEffect(() => {
    checkStatusMutateAsyncRef.current = checkStatus.mutateAsync;
  }, [checkStatus.mutateAsync]);

  const canClose = status === 'completed' || status === 'failed' || status === 'timeout';

  const checkLastSync = async (): Promise<boolean> => {
    try {
      // Check last completed sync and enforce backend-configured rate limiting.
      const response = await getLastSync.mutateAsync();
      const rateLimitMinutes = Math.max(0, response.rateLimitMinutes ?? 0);
      if (rateLimitMinutes <= 0) {
        return true;
      }

      if (response.success && response.closeTime) {
        const lastSyncTime = new Date(response.closeTime);
        const now = new Date();
        const timeSinceLastSync = now.getTime() - lastSyncTime.getTime();
        const minSyncIntervalMs = rateLimitMinutes * 60 * 1000;

        if (timeSinceLastSync < minSyncIntervalMs) {
          const elapsedSeconds = Math.max(0, Math.floor(timeSinceLastSync / 1000));
          const waitSeconds = Math.ceil((minSyncIntervalMs - timeSinceLastSync) / 1000);
          const syncLimitText = formatDuration(rateLimitMinutes * 60);
          notifications.warning(
            'Sync Recently Run',
            `A sync was run ${formatDuration(elapsedSeconds)} ago. ` +
            `The current limit is one sync every ${syncLimitText}. ` +
            `Please wait ${formatDuration(waitSeconds)} before trying again.`
          );
          return false;
        }
      }
      return true;
    } catch (err) {
      // If we can't check, allow the sync to proceed
      log.warn('Failed to check last sync time', { error: err });
      return true;
    }
  };

  const handleStartSync = async () => {
    // Check if a sync was run recently
    const canProceed = await checkLastSync();
    if (!canProceed) {
      return;
    }

    setStatus('starting');
    setError(null);
    setStartTime(new Date());
    setElapsedSeconds(0);
    pollAttemptsRef.current = 0;

    try {
      const response = await triggerSync.mutateAsync({
        updateExisting: true,
        dryRun: false,
      });

      if (response.success && response.workflowId) {
        setWorkflowId(response.workflowId);
        setStatus('running');
      } else {
        setStatus('failed');
        setError(response.error || response.message || 'Failed to start sync workflow');
      }
    } catch (err) {
      setStatus('failed');
      // Extract detailed error information
      if (err && typeof err === 'object' && 'message' in err) {
        const apiError = err as { message?: string; details?: unknown; status?: number; error?: string };
        
        // Handle 409 Conflict (sync already in progress)
        if (apiError.status === 409) {
          setError(apiError.error || apiError.message || 'A sync is already in progress');
        } else {
          const detailsStr = apiError.details ? `: ${JSON.stringify(apiError.details)}` : '';
          setError(`${apiError.message}${detailsStr}`);
        }
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Unknown error occurred. Please check logs for details.');
        log.error('Sync Error', { error: err });
      }
    }
  };

  const checkStatusOnce = useCallback(async () => {
    if (!workflowId) return;

    try {
      const response = await checkStatusMutateAsyncRef.current(workflowId);

      if (response.status === 'completed' && response.result) {
        setResult(response.result);
        setStatus('completed');
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }

        await queryClient.invalidateQueries({ queryKey: ['entities'] });
        onComplete?.();
        return;
      }

      if (response.status === 'failed') {
        setStatus('failed');
        setError('Sync workflow failed');
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
        return;
      }
    } catch (err) {
      log.error('Failed to check sync status', { error: err, workflowId });
      // Don't fail the entire workflow on status check errors
    }
  }, [workflowId, queryClient, onComplete]);

  useEffect(() => {
    if (!workflowId || status !== 'running') {
      return;
    }

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        pollAttemptsRef.current += 1;
        const startedAt = startTime?.getTime() ?? Date.now();
        if (Date.now() - startedAt >= POLL_CONFIG.MAX_POLL_DURATION_MS) {
          setStatus('timeout');
          setError('Sync polling timeout - workflow may still be running.');
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          return;
        }
        await checkStatusOnce();
      } catch (err) {
        setStatus('failed');
        setError(err instanceof Error ? err.message : 'Failed to check sync status');
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      }
    }, POLL_CONFIG.POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [workflowId, status, startTime, checkStatusOnce]);

  // Update elapsed time every second while running
  useEffect(() => {
    if (status !== 'running' || !startTime) {
      return;
    }

    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
      setElapsedSeconds(elapsed);
    }, 1000);

    return () => clearInterval(timer);
  }, [status, startTime]);

  const handleClose = () => {
    if (canClose) {
      // Reset state for next time
      setStatus('idle');
      setWorkflowId(null);
      setResult(null);
      setError(null);
      setStartTime(null);
      pollAttemptsRef.current = 0;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      onClose();
    }
  };

  const getElapsedTime = () => {
    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  return (
    <Dialog
      open={open}
      onClose={canClose ? handleClose : undefined}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={!canClose}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon />
          Person Data Sync
        </Box>
      </DialogTitle>

      <DialogContent>
        {status === 'idle' && (
          <Box>
            <Typography variant="body1" gutterBottom>
              This will synchronize personnel data from the PeopleSync Core API.
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              The sync process will:
              <List dense>
                <ListItem>
                  <ListItemText primary="• Fetch all contact records from PeopleSync" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Create new person entities for new contacts" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Update existing person entities with latest data" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• This may take several minutes depending on the number of records" />
                </ListItem>
              </List>
            </Alert>
          </Box>
        )}

        {status === 'starting' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Starting sync workflow...
            </Typography>
          </Box>
        )}

        {status === 'running' && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body1">
                Syncing personnel data...
              </Typography>
            </Box>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Elapsed time: {getElapsedTime()}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Sync continues on the server. You can close or refresh this page and check status later.
            </Alert>
          </Box>
        )}

        {status === 'completed' && result && (
          <Box>
            {result.errors > 0 ? (
              <Alert severity="warning" icon={<ErrorIcon />} sx={{ mb: 3 }}>
                Sync completed with {result.errors} error{result.errors !== 1 ? 's' : ''}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {result.created + result.updated} record{result.created + result.updated !== 1 ? 's' : ''} processed successfully, but {result.errors} failed. See error details below.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
                Sync completed successfully!
              </Alert>
            )}

            <Typography variant="h6" gutterBottom>
              Sync Results
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip label={`${result.totalFetched} Fetched`} color="primary" />
              <Chip label={`${result.created} Created`} color="success" />
              <Chip label={`${result.updated} Updated`} color="info" />
              <Chip label={`${result.skipped} Skipped`} color="default" />
              {result.errors > 0 && <Chip label={`${result.errors} Errors`} color="error" />}
            </Box>

            <Typography variant="body2" color="text.secondary">
              Duration: {Math.floor(result.durationMs / 1000)}s
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Synced at: {new Date(result.syncedAt).toLocaleString()}
            </Typography>

            {result.errorDetails && result.errorDetails.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Errors ({result.errors}):
                </Typography>
                <Box
                  sx={{
                    maxHeight: 200,
                    overflow: 'auto',
                    bgcolor: 'grey.50',
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  {result.errorDetails.slice(0, 10).map((err, idx) => (
                    <Typography key={idx} variant="caption" display="block">
                      {err.contactId}: {err.error}
                    </Typography>
                  ))}
                  {result.errors > 10 && (
                    <Typography variant="caption" color="text.secondary">
                      ... and {result.errors - 10} more{result.errors > result.errorDetails.length ? ` (showing ${result.errorDetails.length} of ${result.errors} error details)` : ''}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {status === 'failed' && (
          <Alert severity="error" icon={<ErrorIcon />}>
            <Typography variant="body1" gutterBottom>
              Sync failed
            </Typography>
            {error && <Typography variant="body2">{error}</Typography>}
          </Alert>
        )}

        {status === 'timeout' && (
          <Alert severity="warning" icon={<ErrorIcon />}>
            <Typography variant="body1" gutterBottom>
              Sync polling timeout
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The browser stopped polling for updates after 1 hour. The sync workflow may still be running on the server.
            </Typography>
            {error && <Typography variant="body2">{error}</Typography>}
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              You can close this modal and check the sync status manually later if needed.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button size="small" variant="outlined" onClick={() => void checkStatusOnce()}>
                Check Status Now
              </Button>
            </Box>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {status === 'idle' && (
          <>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <ViewerGuardedButton onClick={handleStartSync} variant="contained" startIcon={<SyncIcon />}>
              Start Sync
            </ViewerGuardedButton>
          </>
        )}

        {(status === 'completed' || status === 'failed' || status === 'timeout') && (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// TODO: FUTURE - Implement Error Boundary for PersonSyncModal
// Prevents modal crashes from taking down the entire dialog provider
// This can wrap the modal content when error recovery is needed
